import * as THREE from 'three/webgpu';
import {
	Fn,
	PI,
	PI2,
	acos,
	asin,
	atan,
	cameraPosition,
	clamp,
	cos,
	dot,
	exp,
	float,
	instanceIndex,
	max,
	mix,
	normalize,
	positionWorld,
	pow,
	sin,
	smoothstep,
	texture,
	textureStore,
	uniform,
	vec2,
	vec3,
	vec4
} from 'three/tsl';
import {
	ATMOSPHERE_QUALITY_PRESETS,
	clampAtmosphereParameters,
	createAtmosphereParameters,
	evaluateAtmosphereLighting
} from './AtmosphereParameters.js';

const DEG2RAD = Math.PI / 180;

const airMass = Fn( ( [ cosineZenith ] ) => {

	const safeCosine = max( 0, cosineZenith );
	const zenith = acos( clamp( safeCosine, 0, 1 ) );
	const zenithDegrees = zenith.mul( 180 / Math.PI );
	return float( 1 ).div(
		max( 0.025, safeCosine.add( float( 0.50572 ).mul( pow( float( 96.07995 ).sub( zenithDegrees ), - 1.6364 ) ) ) )
	);

} );

function createTexture( width, height, name ) {

	const target = new THREE.StorageTexture( width, height );
	target.name = name;
	target.type = THREE.HalfFloatType;
	target.format = THREE.RGBAFormat;
	target.colorSpace = THREE.NoColorSpace;
	target.generateMipmaps = false;
	target.mipmapsAutoUpdate = false;
	target.minFilter = THREE.LinearFilter;
	target.magFilter = THREE.LinearFilter;
	target.wrapS = THREE.RepeatWrapping;
	target.wrapT = THREE.ClampToEdgeWrapping;
	return target;

}

function createTransmittanceCompute( target, width, height, uniforms ) {

	const compute = Fn( () => {

		const id = instanceIndex;
		const x = id.mod( width );
		const y = id.div( width );
		const uv = vec2( x, y ).add( 0.5 ).div( vec2( width, height ) );
		const cosineZenith = uv.x.mul( 2 ).sub( 1 );
		const altitude = uv.y;
		const densityFalloff = exp( altitude.mul( - 6.5 ) );
		const path = airMass( cosineZenith ).mul( densityFalloff );
		const betaRayleigh = vec3( 0.0062, 0.0142, 0.0335 ).mul( uniforms.rayleigh );
		const betaMie = vec3( 0.0038 ).mul( uniforms.turbidity ).add( uniforms.mieCoefficient.mul( 0.55 ) );
		const betaOzone = vec3( 0.0035, 0.0065, 0.0012 ).mul( uniforms.ozone );
		const horizonVisibility = smoothstep( - 0.105, 0.006, cosineZenith );
		const transmittance = exp( betaRayleigh.add( betaMie ).add( betaOzone ).mul( path ).negate() )
			.mul( horizonVisibility );
		const maximumTransmittance = max( transmittance.r, max( transmittance.g, transmittance.b ) );

		textureStore( target, vec2( x, y ), vec4( transmittance, maximumTransmittance ) );

	} );

	return compute().compute( width * height ).setName( 'generateAtmosphereTransmittanceLUT' );

}

function createSkyViewCompute( target, width, height, uniforms ) {

	const compute = Fn( () => {

		const id = instanceIndex;
		const x = id.mod( width );
		const y = id.div( width );
		const uv = vec2( x, y ).add( 0.5 ).div( vec2( width, height ) );
		const azimuth = uv.x.sub( 0.5 ).mul( PI2 );
		const elevation = uv.y.sub( 0.5 ).mul( PI );
		const cosElevation = cos( elevation );
		const direction = normalize( vec3( cos( azimuth ).mul( cosElevation ), sin( elevation ), sin( azimuth ).mul( cosElevation ) ) );
		const sunDirection = normalize( uniforms.sunDirection );
		const viewCosine = max( 0, direction.y );
		const viewPath = airMass( viewCosine );
		const betaRayleigh = vec3( 5.804543e-6, 1.356291e-5, 3.02659e-5 ).mul( uniforms.rayleigh );
		const mieBase = vec3( 1.839992e14, 2.779802e14, 4.079048e14 );
		const mieC = uniforms.turbidity.mul( 0.2e-17 );
		const betaMie = mieBase.mul( mieC ).mul( 0.434 ).mul( uniforms.mieCoefficient );
		const betaOzone = vec3( 0.25e-6, 0.48e-6, 0.08e-6 ).mul( uniforms.ozone );
		const rayleighLength = viewPath.mul( 8400 );
		const mieLength = viewPath.mul( 1250 );
		const extinction = exp( betaRayleigh.add( betaOzone ).mul( rayleighLength ).add( betaMie.mul( mieLength ) ).negate() );
		const sunCosine = clamp( sunDirection.y, - 1, 1 );
		const sunVisibility = smoothstep( - 0.105, 0.006, sunCosine );
		const sunEnergy = float( 1000 ).mul( max( 0, float( 1 ).sub( exp( float( 1.611073 ).sub( acos( sunCosine ) ).div( 1.5 ).negate() ) ) ) )
			.mul( sunVisibility );
		const cosineTheta = dot( direction, sunDirection );
		const rayleighPhase = float( 0.0596831 ).mul( float( 1 ).add( pow( cosineTheta.mul( 0.5 ).add( 0.5 ), 2 ) ) );
		const g2 = pow( uniforms.mieDirectionalG, 2 );
		const miePhase = float( 0.0795775 ).mul( float( 1 ).sub( g2 ) )
			.div( pow( float( 1 ).sub( uniforms.mieDirectionalG.mul( cosineTheta ).mul( 2 ) ).add( g2 ), 1.5 ) );
		const scatteringRatio = betaRayleigh.mul( rayleighPhase ).add( betaMie.mul( miePhase ) )
			.div( max( betaRayleigh.add( betaMie ), vec3( 1e-7 ) ) );
		const singleScattering = pow( scatteringRatio.mul( sunEnergy ).mul( extinction.oneMinus() ), vec3( 1.5 ) );
		const sunsetMix = clamp( pow( float( 1 ).sub( max( 0, sunDirection.y ) ), 5 ), 0, 1 );
		const sunsetCorrection = mix(
			vec3( 1 ),
			pow( scatteringRatio.mul( sunEnergy ).mul( extinction ), vec3( 0.5 ) ),
			sunsetMix
		);
		const multipleScattering = extinction.oneMinus()
			.mul( vec3( 0.12, 0.16, 0.24 ) )
			.mul( uniforms.multipleScattering )
			.mul( float( 0.18 ).add( sunVisibility.mul( 0.82 ) ) );
		const twilightStrength = smoothstep( - 0.22, - 0.02, sunCosine )
			.mul( smoothstep( - 0.01, 0.18, sunCosine ).oneMinus() );
		const horizonBand = exp( direction.y.abs().mul( - 5.5 ) );
		const twilightSunLobe = pow( cosineTheta.mul( 0.5 ).add( 0.5 ).clamp(), 8 );
		const twilightColor = mix( vec3( 0.018, 0.032, 0.095 ), vec3( 0.15, 0.048, 0.055 ), twilightSunLobe )
			.mul( twilightStrength )
			.mul( horizonBand.mul( 0.72 ).add( 0.28 ) );
		const night = vec3( 0.0035, 0.006, 0.018 ).mul( float( 1 ).sub( sunVisibility ).mul( 0.8 ).add( 0.2 ) );
		const sky = singleScattering.mul( sunsetCorrection ).mul( 0.04 )
			.add( extinction.mul( 0.004 ) )
			.add( multipleScattering )
			.add( twilightColor )
			.add( night )
			.mul( uniforms.skyIntensity )
			.toVar();
		const ground = uniforms.groundAlbedo.mul( uniforms.multipleScattering.mul( 0.08 ).add( 0.018 ) )
			.add( sky.mul( 0.08 ) );
		const aboveHorizon = smoothstep( - 0.025, 0.015, direction.y );
		sky.assign( mix( ground, sky, aboveHorizon ) );

		textureStore( target, vec2( x, y ), vec4( sky, 1 ) );

	} );

	return compute().compute( width * height ).setName( 'generateAtmosphereSkyViewLUT' );

}

function createSkyMaterial( skyTexture, uniforms ) {

	const skyView = texture( skyTexture, null, 0 );
	const material = new THREE.MeshBasicNodeMaterial();
	material.name = 'CyborgClouds compute atmosphere sky';
	material.side = THREE.BackSide;
	material.depthWrite = false;
	material.depthTest = false;
	material.transparent = false;
	material.userData.skyViewNode = skyView;
	material.colorNode = Fn( () => {

		const direction = normalize( positionWorld.sub( cameraPosition ) );
		const skyUV = vec2(
			atan( direction.z, direction.x ).div( PI2 ).add( 0.5 ),
			asin( clamp( direction.y, - 1, 1 ) ).div( PI ).add( 0.5 )
		);
		const radiance = skyView.sample( skyUV ).rgb.toVar();
		const sunAlignment = dot( direction, normalize( uniforms.sunDirection ) );
		const sunDisc = smoothstep( 0.99994, 0.999989, sunAlignment );
		const sunHalo = pow( max( sunAlignment, 0 ), 512 ).mul( 0.12 );
		const solarRadiance = uniforms.sunTransmittance.mul( uniforms.sunRadiance )
			.mul( sunDisc.mul( 18 ).add( sunHalo ) );
		radiance.addAssign( solarRadiance );

		return vec4( radiance, 1 );

	} )();

	return material;

}

/**
 * Compute-generated, WebGPU-only atmosphere for Three.js. The implementation
 * follows the production LUT split (transmittance + sky view) while using a
 * compact real-time scattering approximation suitable for this package.
 */
export class WebGPUAtmosphere extends THREE.Group {

	constructor( renderer, options = {} ) {

		super();

		if ( ! renderer?.isWebGPURenderer ) throw new TypeError( 'WebGPUAtmosphere requires a Three.js WebGPURenderer.' );

		this.name = options.name ?? 'CyborgClouds atmosphere';
		this.renderer = renderer;
		this.parameters = createAtmosphereParameters( { quality: options.quality, ...options.parameters } );
		this.uniforms = {
			sunDirection: uniform( new THREE.Vector3() ),
			rayleigh: uniform( this.parameters.rayleigh ),
			turbidity: uniform( this.parameters.turbidity ),
			mieCoefficient: uniform( this.parameters.mieCoefficient ),
			mieDirectionalG: uniform( this.parameters.mieDirectionalG ),
			ozone: uniform( this.parameters.ozone ),
			multipleScattering: uniform( this.parameters.multipleScattering ),
			groundAlbedo: uniform( new THREE.Color( this.parameters.groundAlbedo ) ),
			skyIntensity: uniform( this.parameters.skyIntensity ),
			sunTransmittance: uniform( new THREE.Color( 1, 1, 1 ) ),
			sunRadiance: uniform( 1 )
		};
		this._initialized = false;
		this._dirty = true;
		this._pending = null;
		this._generation = 0;
		this._animationAccumulator = 0;
		this.lastComputeMs = 0;
		this._createResources();
		this.material = createSkyMaterial( this.skyViewTexture, this.uniforms );
		this.sky = new THREE.Mesh( new THREE.SphereGeometry( options.radius ?? 1800, 64, 32 ), this.material );
		this.sky.name = 'Compute atmosphere skydome';
		this.sky.renderOrder = - 100;
		this.sky.frustumCulled = false;
		this.add( this.sky );
		this._syncUniforms();

	}

	_createResources() {

		const preset = ATMOSPHERE_QUALITY_PRESETS[ this.parameters.quality ];
		this.transmittanceTexture = createTexture( preset.transmittanceWidth, preset.transmittanceHeight, 'Atmosphere transmittance LUT' );
		this.skyViewTexture = createTexture( preset.skyWidth, preset.skyHeight, 'Atmosphere sky-view LUT' );
		this.transmittanceCompute = createTransmittanceCompute(
			this.transmittanceTexture,
			preset.transmittanceWidth,
			preset.transmittanceHeight,
			this.uniforms
		);
		this.skyViewCompute = createSkyViewCompute( this.skyViewTexture, preset.skyWidth, preset.skyHeight, this.uniforms );

	}

	_disposeResources() {

		this.transmittanceCompute?.dispose();
		this.skyViewCompute?.dispose();
		this.transmittanceTexture?.dispose();
		this.skyViewTexture?.dispose();

	}

	_syncUniforms() {

		const p = this.parameters;
		const lighting = evaluateAtmosphereLighting( p );
		this.lighting = lighting;
		this.uniforms.sunDirection.value.copy( lighting.sunDirection );
		this.uniforms.rayleigh.value = p.rayleigh;
		this.uniforms.turbidity.value = p.turbidity;
		this.uniforms.mieCoefficient.value = p.mieCoefficient;
		this.uniforms.mieDirectionalG.value = p.mieDirectionalG;
		this.uniforms.ozone.value = p.ozone;
		this.uniforms.multipleScattering.value = p.multipleScattering;
		this.uniforms.groundAlbedo.value.set( p.groundAlbedo );
		this.uniforms.skyIntensity.value = p.skyIntensity;
		this.uniforms.sunTransmittance.value.copy( lighting.sunTransmittance );
		this.uniforms.sunRadiance.value = p.sunIntensity;

	}

	async initialize() {

		if ( this._initialized ) return this;
		await this.renderer.init();
		await this._dispatch();
		this._initialized = true;
		return this;

	}

	async _dispatch() {

		if ( this._pending ) return this._pending;
		const generation = this._generation;
		this._dirty = false;
		const start = performance.now();
		this._pending = this.renderer.computeAsync( [ this.transmittanceCompute, this.skyViewCompute ] )
			.then( () => {

				this.lastComputeMs = performance.now() - start;
				if ( generation !== this._generation ) this._dirty = true;

			} )
			.finally( () => {

				this._pending = null;

			} );
		return this._pending;

	}

	setParameters( patch = {} ) {

		const previousQuality = this.parameters.quality;
		const lutIndependentKeys = new Set( [
			'automaticLighting', 'autoExposure', 'aerialDensity', 'exposure',
			'animateTime', 'timeScale', 'sunIntensity', 'sunsetStartElevation',
			'sunsetWarmth', 'sunsetBrightness'
		] );
		const affectsLUT = Object.keys( patch ).some( ( key ) => ! lutIndependentKeys.has( key ) );
		this.parameters = clampAtmosphereParameters( { ...this.parameters, ...patch } );
		if ( this.parameters.quality !== previousQuality ) {

			this._disposeResources();
			this._createResources();
			if ( this.material ) {

				this.material.userData.skyViewNode.value = this.skyViewTexture;
				this.material.needsUpdate = true;

			}
		}
		this._syncUniforms();
		if ( affectsLUT ) {

			this._generation ++;
			this._dirty = true;

		}
		return this;

	}

	setQuality( quality ) {

		return this.setParameters( { quality } );

	}

	update( camera, deltaSeconds = 0 ) {

		if ( camera?.isCamera ) this.position.copy( camera.position );
		if ( this.parameters.animateTime && deltaSeconds > 0 ) {

			this._animationAccumulator += deltaSeconds;
			if ( this._animationAccumulator >= 0.12 ) {

				const elapsed = this._animationAccumulator;
				this._animationAccumulator = 0;
				const azimuth = ( this.parameters.sunAzimuth + elapsed * this.parameters.timeScale ) % 360;
				const phase = azimuth * DEG2RAD;
				const elevation = Math.sin( phase - Math.PI * 0.5 ) * 54 + 10;
				this.parameters = clampAtmosphereParameters( { ...this.parameters, sunAzimuth: azimuth, sunElevation: elevation } );
				this._syncUniforms();
				this._generation ++;
				this._dirty = true;

			}

		}
		if ( this._initialized && this._dirty && ! this._pending ) void this._dispatch();

	}

	getLighting() {

		return this.lighting;

	}

	applyLighting( targets = {} ) {

		const lighting = this.lighting;
		if ( ! this.parameters.automaticLighting ) {

			targets.clouds?.setSunDirection?.( null );
			targets.clouds?.setEnvironmentLighting?.( null );
			targets.pipeline?.setAerialPerspective?.( { enabled: false } );
			return lighting;

		}

		targets.clouds?.setSunDirection?.( lighting.sunDirection );
		targets.clouds?.setEnvironmentLighting?.( {
			sunColor: lighting.sunColor,
			ambientTop: lighting.ambientTop,
			ambientBottom: lighting.ambientBottom,
			directIntensity: lighting.directIntensity,
			ambientIntensity: 1
		} );
		if ( targets.sunLight?.isDirectionalLight ) {

			targets.sunLight.position.copy( lighting.sunDirection ).multiplyScalar( 500 );
			targets.sunLight.color.copy( lighting.sunColor );
			targets.sunLight.intensity = 3.2 * lighting.directIntensity;

		}
		if ( targets.hemisphereLight?.isHemisphereLight ) {

			targets.hemisphereLight.color.copy( lighting.ambientTop );
			targets.hemisphereLight.groundColor.copy( lighting.ambientBottom );
			targets.hemisphereLight.intensity = 1.35 + lighting.daylight * 0.75;

		}
		targets.pipeline?.setAerialPerspective?.( {
			color: lighting.horizonColor,
			density: lighting.aerialDensity,
			enabled: true
		} );
		( targets.renderer ?? this.renderer ).toneMappingExposure = lighting.exposure;
		return lighting;

	}

	getStats() {

		const preset = ATMOSPHERE_QUALITY_PRESETS[ this.parameters.quality ];
		return {
			quality: this.parameters.quality,
			skyWidth: preset.skyWidth,
			skyHeight: preset.skyHeight,
			transmittanceWidth: preset.transmittanceWidth,
			transmittanceHeight: preset.transmittanceHeight,
			approximateTextureBytes: ( preset.skyWidth * preset.skyHeight + preset.transmittanceWidth * preset.transmittanceHeight ) * 8,
			lastComputeMs: this.lastComputeMs
		};

	}

	dispose() {

		this.sky.geometry.dispose();
		this.material.dispose();
		this._disposeResources();

	}

}
